import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { NotificationService } from '../components/notification/notification.service';
import { Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { SpinnerService } from '../components/spinner/spinner.service';
import { NotificationType } from '../components/notification/notification-type.enum';
import { throwError } from 'rxjs/internal/observable/throwError';
import { Router } from '@angular/router';

type Notify = 'ALL' | 'NONE' | 'ERROR' | 'SUCCESS';
type ResponseType = 'json' | 'blob';

export interface Params {
    params?: HttpParams;
    headers?: HttpHeaders;
    responseType?: ResponseType;
    retryCount?: number;
    notify?: Notify;
    timeout?: number;
    spin?: boolean;
    successMessage?: string;
    errorMessage?: string;
}

@Injectable()
export class HttpService {
    private sessionExpiredCallbacks: (() => void)[] = [];

    public constructor(
        private http: HttpClient,
        private notification: NotificationService,
        private spinner: SpinnerService,
        private router: Router,
    ) {}

    public get<T>(
        url: string,
        {
            params = new HttpParams(),
            headers = new HttpHeaders(),
            responseType = 'json',
            notify = 'ALL',
            timeout = 5000,
            spin = true,
        }: Params = {},
    ): Observable<T> {
        return this.requestPipe<T>(
            this.http.get<T>(url, { params, headers, responseType: responseType as 'json' }),
            notify,
            timeout,
            spin,
        );
    }

    public put<T>(
        url: string,
        data: unknown,
        {
            params = new HttpParams(),
            headers = new HttpHeaders(),
            notify = 'ALL',
            timeout = 5000,
            spin = true,
        }: Params = {},
    ): Observable<T> {
        return this.requestPipe<T>(this.http.put<T>(url, data, { params, headers }), notify, timeout, spin);
    }

    public post<T>(
        url: string,

        data: unknown,
        {
            params = new HttpParams(),
            responseType = 'json',
            headers = new HttpHeaders(),
            notify = 'ALL',
            timeout = 5000,
            spin = true,
        }: Params = {},
    ): Observable<T> {
        return this.requestPipe<T>(
            this.http.post<T>(url, data, { params, headers, responseType: responseType as 'json' }),
            notify,
            timeout,
            spin,
        );
    }

    public upload(
        url: string,
        data: FormData,
        { params = new HttpParams(), notify = 'ALL', timeout = 5000, spin = true }: Params = {},
    ): Observable<unknown> {
        return this.requestPipe<unknown>(this.http.post(url, data, { params }), notify, timeout, spin);
    }

    public delete<T>(
        url: string,
        {
            params = new HttpParams(),
            headers = new HttpHeaders(),
            notify = 'ALL',
            timeout = 5000,
            spin = true,
        }: Params = {},
    ): Observable<T> {
        return this.requestPipe<T>(this.http.delete<T>(url, { params, headers }), notify, timeout, spin);
    }

    private requestPipe<T>(
        req: Observable<T>,
        notify: Notify,
        timeout: number,
        spin: boolean,
        successMessage: string = 'Deine Daten wurden erfolgreich gespeichert',
        errorMessage: string = 'Es ist ein Fehler aufgetreten, bitte versuche es erneut',
    ): Observable<T> {
        if (spin === true) {
            this.spinner.start();
        }
        return req.pipe(
            finalize(() => {
                if (spin === true) {
                    this.spinner.stop();
                }
            }),
            tap(() => {
                if (notify === 'ALL' || notify === 'SUCCESS') {
                    this.notification.notify(successMessage, NotificationType.SUCCESS, timeout);
                }
            }),
            catchError((err, caught) => this.handleError(err, caught, notify, timeout, errorMessage)),
        );
    }

    public addSessionExpiredCallback(fn: () => void): void {
        this.sessionExpiredCallbacks.push(fn);
    }

    private handleError(
        err: HttpErrorResponse,
        caught: Observable<unknown>,
        notify: Notify,
        timeout: number,
        errorMessage: string,
    ): Observable<never> {
        const doNotify = notify === 'ALL' || notify === 'ERROR';

        if (err.error instanceof ErrorEvent) {
            // client side error
            if (doNotify) {
                this.notification.notify(errorMessage, NotificationType.CRITICAL, timeout);
            }
        } else {
            // TODO: Match error response and notify user
            if (err.error.ErrorCode) {
                switch (err.error.ErrorCode) {
                    case 72: // email address already in use (response to contract request)
                        if (doNotify) {
                            this.notification.notify(
                                'Für diese E-Mail-Adresse besteht bereits ein Kundenkonto',
                                NotificationType.WARNING,
                                timeout,
                            );
                        }
                        break;
                    // 22 unknown User
                    case 22:
                        if (doNotify) {
                            this.notification.notify(
                                'Unbekannter Benutzer oder falsches Kennwort.',
                                NotificationType.WARNING,
                                timeout,
                            );
                        }
                        break;
                    case 9: // Session abgelaufen
                    case 10: // Session unbekannt
                        if (doNotify) {
                            this.notification.notify(
                                'Session ungültig. Bitte logge dich ein.',
                                NotificationType.WARNING,
                                timeout,
                            );
                        }
                        // This is a very hacky solution to avoid circular dependencies
                        // it should be replaced by using the updated http service and
                        // an intercept fn from the library!
                        this.sessionExpiredCallbacks.forEach((fn: () => void) => fn());
                        this.router.navigate(['/login'], {
                            queryParams: { redirect: this.router.routerState.snapshot.url },
                        });
                        break;
                    case 46: // user for pw-reset not found
                        if (doNotify) {
                            this.notification.notify(
                                'Benutzer nicht gefunden. Bitte überprüfe die eingegebene E-Mail-Adresse und Versicherungsnummer.',
                                NotificationType.WARNING,
                                timeout,
                            );
                        }
                        break;
                    case 47: // pw reset token not valid/expired
                        if (doNotify) {
                            this.notification.notify(
                                'Dieser Link ist abgelaufen oder ungültig.',
                                NotificationType.WARNING,
                                timeout,
                            );
                        }
                        return throwError(() => new Error('token invalid or expired'));
                    case 78: // blacklisted reseller
                        if (doNotify) {
                            this.notification.notify(
                                'Über diesen Vermittler ist leider kein Abschluss möglich',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 96: // no file attached
                        if (doNotify) {
                            this.notification.notify(
                                'Die Anfrage enthält keine Datei',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 97: // file too small
                        if (notify) {
                            this.notification.notify(
                                'Die angehängte Datei ist leer oder zu klein',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 98: // file too big
                        if (notify) {
                            this.notification.notify(
                                'Die angehängte Datei ist zu groß',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 99: // invalid file extension
                        if (notify) {
                            this.notification.notify(
                                'Die Dateiendung der angehängten Datei ist nicht zulässig',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 100: // content does not match file extension
                        if (notify) {
                            this.notification.notify(
                                'Konflikt zwischen dem Inhalt und der Dateiendung der angehängten Datei',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 101: // HEIC/HEIF image
                        if (notify) {
                            this.notification.notify(
                                'Bilder im HEIC- oder HEIF-Format sind nicht zugelassen',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 102: // file damaged
                        if (notify) {
                            this.notification.notify(
                                'Eine angehängte Datei ist beschädigt oder kann nicht gelesen werden.',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 103: // file is password protected
                        if (notify) {
                            this.notification.notify(
                                'Eine angehängte Datei ist mit einem Passwort geschützt und kann nicht verarbeitet werden. Bitte lade das Dokument ohne Passwortschutz hoch.',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    case 104: // image resolution too small
                        if (notify) {
                            this.notification.notify(
                                'Die Auflösung einer angehängten Datei ist zu niedrig. Bitte lade ein höher aufgelöstes Bild hoch.',
                                NotificationType.CRITICAL,
                                timeout,
                            );
                        }
                        break;
                    default:
                        if (doNotify) {
                            this.notification.notify(errorMessage, NotificationType.CRITICAL, timeout);
                        }
                        break;
                }
            } else {
                if (doNotify) {
                    this.notification.notify(errorMessage, NotificationType.CRITICAL, timeout);
                }
            }
        }

        // this would be returned as error message and might be handled by the caller, e.g. shown to the user.
        return throwError(() => err.error);
    }
}
